import { ChangeDetectionStrategy, Component, inject, Input, input, OnInit } from '@angular/core';
import { ComponentStatus, StateComponent } from '../../components/state/state.component';
import { ActivatedRoute, Router } from '@angular/router';
import { TournamentApiService } from '../../apis/tournament/tournament-api.service';
import { MatDialog } from '@angular/material/dialog';
import { NotificationService } from '../../services/notification.service';
import { CompetitionGroupCommonService } from '../../../sections/competition-group/pages/competition-group-pages/common/services/competition-group-common.service';
import { AnalyticsService } from '../../services/analytics.service';
import { ITournamentTeamModel } from '../../apis/tournament/model/tournament.model';
import { MatCard, MatCardContent } from '@angular/material/card';
import { TeamPlayersListComponent } from './team-player-list/team-players-list.component';
import { MatAccordion, MatExpansionPanel, MatExpansionPanelDescription, MatExpansionPanelHeader, MatExpansionPanelTitle } from '@angular/material/expansion';
import { NgOptimizedImage } from '@angular/common';
import { LoadingContainerComponent } from '../../components/loading-container/loading-container.component';

@Component({
  selector: 'oo-teams-list',
  templateUrl: './teams-list.component.html',
  styleUrls: ['./teams-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    LoadingContainerComponent,
    MatAccordion,
    MatExpansionPanel,
    MatExpansionPanelHeader,
    MatExpansionPanelTitle,
    NgOptimizedImage,
    MatExpansionPanelDescription,
    TeamPlayersListComponent,
    MatCard,
    MatCardContent,
  ],
})
export class TeamsListComponent extends StateComponent<TeamsManagementComponentState> implements OnInit {

  tournamentId = input.required<string>();
  state: TeamsManagementComponentState = {
    status: 'ready',
    teams: [],
    currentSelectedTeamId: null,
    prevSelectedTeamId: null,
    isManageable: false,
  };
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly tourApi = inject(TournamentApiService);
  private readonly dialog = inject(MatDialog);
  private readonly notificationService = inject(NotificationService);
  private readonly tournamentCommonService = inject(CompetitionGroupCommonService);
  private readonly analyticsService = inject(AnalyticsService);

  @Input() set isManageable(isManageable: boolean) {
    this.updateState({
      isManageable,
    });
  }

  ngOnInit(): void {
    this.analyticsService.trackScreenName('tournamentConfiguration');
    this.fetchTeams();
  }

  teamOpened(teamId: number): void {
    this.updateState({
      prevSelectedTeamId: this.state.currentSelectedTeamId,
      currentSelectedTeamId: teamId,
    });
  }

  toggleIsActive(teamId: number, isActive: boolean): void {
    const index = this.state.teams.findIndex(({ id }) => id === teamId);
    const team = this.state.teams[index];
    const currentState = team.isActive;
    team.isActive = isActive;

    this.tourApi.updateTeam(this.tournamentId(), teamId, { isActive })
      .catch((e: Error) => {
        console.error(e);
        this.notificationService.notifyError();
        this.state.teams[index] = {
          ...this.state.teams[index],
          isActive: currentState,
        };

        this.updateState({ teams: [...this.state.teams] });
      });
  }

  reload(): void {
    this.fetchTeams();
  }

  private fetchTeams(): void {
    this.updateStatus('loading');
    this.tourApi.getTournamentTeams(this.tournamentId())
      .then((teams) => {
        this.updateState({
            status: 'ready',
            teams: teams.sort((a, b) => a.name < b.name ? -1 : 1)
              .map((team) => ({
                ...team,
                players: [],
              })),
          },
        );
      })
      .catch((e: Error) => {
        console.error(e);
        this.updateStatus('error');
      });
  }
}

interface TeamsManagementComponentState {
  status: ComponentStatus;
  teams: ITournamentTeamModel[];
  currentSelectedTeamId: number | null;
  prevSelectedTeamId: number | null;
  isManageable: boolean;
}
